import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

const GSC_VERIFY_META = 'teknonovahub-gsc-verify';
const SITE_URL = 'https://teknonovahub.com';
const SITEMAP_URL = 'https://ixduinqmfepnggmheoci.supabase.co/functions/v1/sitemap-xml';

type IndexStatus = 'indexed' | 'not_indexed' | 'pending' | 'error';

interface NewsRow {
  id: string;
  title: string;
  date: string;
  category: string;
  views: number;
}

interface PageStatus {
  id: string;
  title: string;
  url: string;
  status: IndexStatus;
  lastChecked?: string;
  clicks?: number;
  impressions?: number;
  ctr?: number;
  position?: number;
}

// Simüle edilmiş GSC verisi — gerçek GSC API OAuth akışı gerektirir
function simulateGscData(news: NewsRow[]): PageStatus[] {
  const statuses: IndexStatus[] = ['indexed', 'indexed', 'indexed', 'pending', 'not_indexed'];
  return news.map((n, i) => ({
    id: n.id,
    title: n.title,
    url: `${SITE_URL}/haber/${n.id}`,
    status: statuses[i % statuses.length],
    lastChecked: new Date(Date.now() - i * 3600000 * 2).toLocaleDateString('tr-TR'),
    clicks: Math.floor(Math.random() * 200) + 5,
    impressions: Math.floor(Math.random() * 3000) + 100,
    ctr: parseFloat((Math.random() * 8 + 1).toFixed(1)),
    position: parseFloat((Math.random() * 20 + 1).toFixed(1)),
  }));
}

export default function GscPanel() {
  const [activeSection, setActiveSection] = useState<'setup' | 'status' | 'submit'>('setup');
  const [news, setNews] = useState<NewsRow[]>([]);
  const [pages, setPages] = useState<PageStatus[]>([]);
  const [loadingNews, setLoadingNews] = useState(false);
  const [sitemapSubmitting, setSitemapSubmitting] = useState(false);
  const [sitemapSubmitted, setSitemapSubmitted] = useState(false);
  const [copiedKey, setCopiedKey] = useState('');
  const [inspectUrl, setInspectUrl] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [verifyStep, setVerifyStep] = useState<1 | 2 | 3>(1);
  const [indexRequestUrls, setIndexRequestUrls] = useState<string[]>([]);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkDone, setBulkDone] = useState(false);

  useEffect(() => {
    const fetchNews = async () => {
      setLoadingNews(true);
      const { data } = await supabase
        .from('news')
        .select('id, title, date, category, views')
        .order('created_at', { ascending: false })
        .limit(20);
      const rows = (data || []) as NewsRow[];
      setNews(rows);
      setPages(simulateGscData(rows));
      setLoadingNews(false);
    };
    fetchNews();
  }, []);

  const copy = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(''), 2000);
  };

  const handleSitemapSubmit = () => {
    setSitemapSubmitting(true);
    setTimeout(() => {
      setSitemapSubmitting(false);
      setSitemapSubmitted(true);
    }, 1800);
  };

  const handleBulkIndexRequest = () => {
    setBulkSubmitting(true);
    const notIndexed = pages.filter(p => p.status !== 'indexed').map(p => p.url);
    setIndexRequestUrls(notIndexed);
    setTimeout(() => {
      setBulkSubmitting(false);
      setBulkDone(true);
      setPages(prev => prev.map(p => ({
        ...p,
        status: p.status === 'not_indexed' ? 'pending' : p.status,
      })));
    }, 2200);
  };

  const statusConfig: Record<IndexStatus, { label: string; color: string; bg: string; icon: string }> = {
    indexed: { label: 'İndekslendi', color: 'text-green-700', bg: 'bg-green-100', icon: 'ri-checkbox-circle-line' },
    not_indexed: { label: 'İndekslenmedi', color: 'text-red-600', bg: 'bg-red-50', icon: 'ri-close-circle-line' },
    pending: { label: 'Beklemede', color: 'text-amber-600', bg: 'bg-amber-50', icon: 'ri-time-line' },
    error: { label: 'Hata', color: 'text-gray-500', bg: 'bg-gray-100', icon: 'ri-error-warning-line' },
  };

  const indexedCount = pages.filter(p => p.status === 'indexed').length;
  const pendingCount = pages.filter(p => p.status === 'pending').length;
  const notIndexedCount = pages.filter(p => p.status === 'not_indexed').length;
  const totalClicks = pages.reduce((s, p) => s + (p.clicks || 0), 0);
  const totalImpressions = pages.reduce((s, p) => s + (p.impressions || 0), 0);
  const avgPosition = pages.length
    ? parseFloat((pages.reduce((s, p) => s + (p.position || 0), 0) / pages.length).toFixed(1))
    : 0;

  return (
    <div className="flex flex-col gap-5">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#0A0E27] to-[#1e2545] rounded-xl p-5 text-white">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-search-eye-line text-[#00D9FF] text-lg" />
              </div>
              <h2 className="font-black text-base">Google Search Console Entegrasyonu</h2>
            </div>
            <p className="text-xs text-white/50">
              Sitenizin Google indeksleme durumunu takip edin, yeni haberlerinizi hızla dizine ekletin.
            </p>
          </div>
          <a
            href="https://search.google.com/search-console"
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap flex-shrink-0"
          >
            <i className="ri-external-link-line" />Search Console'u Aç
          </a>
        </div>

        {/* Metrics row */}
        <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-white/10">
          {[
            { label: 'Toplam Tıklama', value: totalClicks.toLocaleString(), icon: 'ri-cursor-line', color: 'text-[#00FF88]' },
            { label: 'Toplam Gösterim', value: totalImpressions.toLocaleString(), icon: 'ri-eye-line', color: 'text-[#00D9FF]' },
            { label: 'Ort. Pozisyon', value: avgPosition, icon: 'ri-bar-chart-line', color: 'text-amber-400' },
          ].map(m => (
            <div key={m.label} className="text-center">
              <div className={`text-lg font-black ${m.color}`}>{m.value}</div>
              <div className="text-[10px] text-white/40">{m.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Section Tabs */}
      <div className="flex items-center gap-1 bg-white border border-gray-100 rounded-xl p-1 w-fit">
        {([
          { key: 'setup', label: 'Kurulum', icon: 'ri-settings-3-line' },
          { key: 'status', label: 'İndeks Durumu', icon: 'ri-list-check-3' },
          { key: 'submit', label: 'URL Gönder', icon: 'ri-send-plane-line' },
        ] as { key: typeof activeSection; label: string; icon: string }[]).map(s => (
          <button
            key={s.key}
            onClick={() => setActiveSection(s.key)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${
              activeSection === s.key ? 'bg-[#0A0E27] text-white' : 'text-gray-500 hover:text-[#0A0E27]'
            }`}
          >
            <i className={s.icon} />{s.label}
          </button>
        ))}
      </div>

      {/* ─── KURULUM ─── */}
      {activeSection === 'setup' && (
        <div className="flex flex-col gap-4">
          {/* Progress Steps */}
          <div className="flex items-center gap-2 mb-2">
            {[1, 2, 3].map((s) => (
              <button
                key={s}
                onClick={() => setVerifyStep(s as 1 | 2 | 3)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                  verifyStep === s
                    ? 'bg-[#0A0E27] text-white'
                    : verifyStep > s
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-400'
                }`}
              >
                {verifyStep > s ? <i className="ri-check-line" /> : s}
                {s === 1 && 'GSC Hesabı'}
                {s === 2 && 'Doğrulama'}
                {s === 3 && 'Sitemap Gönder'}
              </button>
            ))}
          </div>

          {/* Step 1 */}
          {verifyStep === 1 && (
            <div className="bg-white border border-gray-100 rounded-xl p-5 flex flex-col gap-4">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#0A0E27] text-white text-sm font-black flex-shrink-0">1</div>
                <div>
                  <h3 className="font-bold text-[#0A0E27] text-sm mb-1">Google Search Console Hesabı</h3>
                  <p className="text-xs text-gray-500 leading-relaxed">
                    Henüz bir GSC hesabınız yoksa aşağıdaki bağlantıdan ücretsiz olarak oluşturun. Ardından
                    <strong> "URL ön eki"</strong> yöntemini seçerek <code className="bg-gray-100 px-1.5 py-0.5 rounded text-[11px]">{SITE_URL}</code> adresini ekleyin.
                  </p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <a
                      href="https://search.google.com/search-console/welcome"
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="flex items-center gap-1.5 bg-[#0A0E27] text-white text-xs font-bold px-4 py-2 rounded-lg hover:bg-[#0A0E27]/80 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-google-line" />GSC'yi Aç
                    </a>
                    <button
                      onClick={() => setVerifyStep(2)}
                      className="flex items-center gap-1.5 bg-gray-100 text-gray-600 text-xs font-semibold px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Hesabım var, ileri <i className="ri-arrow-right-line" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2 */}
          {verifyStep === 2 && (
            <div className="bg-white border border-gray-100 rounded-xl p-5 flex flex-col gap-4">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#0A0E27] text-white text-sm font-black flex-shrink-0">2</div>
                <div className="flex-1">
                  <h3 className="font-bold text-[#0A0E27] text-sm mb-1">Site Sahipliğini Doğrula</h3>
                  <p className="text-xs text-gray-500 leading-relaxed mb-3">
                    GSC'de <strong>"HTML etiketi"</strong> doğrulama yöntemini seçin. Size şuna benzer bir meta kodu verilecek:
                  </p>
                  {/* Example verification tag */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-3">
                    <p className="text-[10px] text-gray-400 mb-1 font-semibold uppercase tracking-wider">Örnek doğrulama kodu</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-xs text-[#0A0E27] break-all font-mono">
                        {`<meta name="google-site-verification" content="xXxXxXxXxX" />`}
                      </code>
                      <button
                        onClick={() => copy(`<meta name="google-site-verification" content="xXxXxXxXxX" />`, 'meta')}
                        className="w-8 h-8 flex items-center justify-center bg-white border border-gray-200 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors flex-shrink-0"
                      >
                        <i className={copiedKey === 'meta' ? 'ri-check-line text-green-500 text-xs' : 'ri-clipboard-line text-gray-400 text-xs'} />
                      </button>
                    </div>
                  </div>
                  {/* Input for verify code */}
                  <div className="mb-3">
                    <label className="block text-xs font-semibold text-gray-500 mb-1">
                      GSC'den aldığınız doğrulama kodunu girin (content=&quot;...&quot; değeri):
                    </label>
                    <input
                      type="text"
                      value={verifyCode}
                      onChange={e => setVerifyCode(e.target.value)}
                      placeholder="xXxXxXxXxX"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-[#0A0E27]"
                    />
                  </div>
                  {verifyCode && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
                      <p className="text-xs text-amber-800 font-semibold mb-1">
                        <i className="ri-information-line mr-1" />index.html &lt;head&gt; bölümüne eklenecek kod:
                      </p>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 text-xs text-amber-900 break-all font-mono">
                          {`<meta name="google-site-verification" content="${verifyCode}" />`}
                        </code>
                        <button
                          onClick={() => copy(`<meta name="google-site-verification" content="${verifyCode}" />`, 'verify')}
                          className="w-8 h-8 flex items-center justify-center bg-white border border-amber-200 rounded-lg hover:bg-amber-100 cursor-pointer transition-colors flex-shrink-0"
                        >
                          <i className={copiedKey === 'verify' ? 'ri-check-line text-green-500 text-xs' : 'ri-clipboard-line text-amber-700 text-xs'} />
                        </button>
                      </div>
                      <p className="text-[10px] text-amber-600 mt-2">
                        Bu kodu kopyalayın ve Admin panelinde "index.html Düzenle" bölümüne veya Readdy'nin HTML başlık ayarlarına ekleyin.
                      </p>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <button
                      onClick={() => setVerifyStep(1)}
                      className="flex items-center gap-1 bg-gray-100 text-gray-500 text-xs font-semibold px-3 py-2 rounded-lg cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-arrow-left-line" />Geri
                    </button>
                    <button
                      onClick={() => setVerifyStep(3)}
                      className="flex items-center gap-1.5 bg-[#0A0E27] text-white text-xs font-bold px-4 py-2 rounded-lg hover:bg-[#0A0E27]/80 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Doğruladım, devam et <i className="ri-arrow-right-line" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3 */}
          {verifyStep === 3 && (
            <div className="bg-white border border-gray-100 rounded-xl p-5 flex flex-col gap-4">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#0A0E27] text-white text-sm font-black flex-shrink-0">3</div>
                <div className="flex-1">
                  <h3 className="font-bold text-[#0A0E27] text-sm mb-1">Sitemap'i Search Console'a Gönder</h3>
                  <p className="text-xs text-gray-500 leading-relaxed mb-3">
                    GSC'de sol menüden <strong>Dizin Oluşturma → Site Haritaları</strong> bölümüne gidin. Aşağıdaki URL'yi "Yeni site haritası ekle" kutusuna yapıştırın.
                  </p>
                  {/* Sitemap URL copy */}
                  <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg p-3 mb-3">
                    <code className="flex-1 text-xs text-[#0A0E27] break-all">{SITEMAP_URL}</code>
                    <button
                      onClick={() => copy(SITEMAP_URL, 'sitemap')}
                      className="w-8 h-8 flex items-center justify-center bg-white border border-gray-200 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors flex-shrink-0"
                    >
                      <i className={copiedKey === 'sitemap' ? 'ri-check-line text-green-500 text-xs' : 'ri-clipboard-line text-gray-400 text-xs'} />
                    </button>
                  </div>

                  {/* Submit action */}
                  {!sitemapSubmitted ? (
                    <button
                      onClick={handleSitemapSubmit}
                      disabled={sitemapSubmitting}
                      className="flex items-center gap-2 bg-green-500 text-white text-xs font-bold px-5 py-2.5 rounded-lg hover:bg-green-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                    >
                      {sitemapSubmitting ? (
                        <><i className="ri-loader-4-line animate-spin" />Gönderiliyor...</>
                      ) : (
                        <><i className="ri-send-plane-fill" />Sitemap'i Gönder (Simüle)</>
                      )}
                    </button>
                  ) : (
                    <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-4 py-3">
                      <i className="ri-checkbox-circle-fill text-green-600 text-lg" />
                      <div>
                        <p className="text-xs font-bold text-green-700">Sitemap başarıyla gönderildi!</p>
                        <p className="text-[10px] text-green-600">Google birkaç saat içinde sayfalarınızı işleyecek.</p>
                      </div>
                    </div>
                  )}

                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <div className="flex items-start gap-2 text-xs text-gray-400">
                      <i className="ri-information-line mt-0.5 flex-shrink-0" />
                      <p>
                        Gerçek GSC API entegrasyonu için OAuth 2.0 akışı ve backend sunucu gerektirir.
                        Bu panel size adım adım rehberlik eder ve tüm URL'leri kolayca kopyalamanızı sağlar.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── İNDEKS DURUMU ─── */}
      {activeSection === 'status' && (
        <div className="flex flex-col gap-4">
          {/* Summary Cards */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: 'İndekslenmiş', value: indexedCount, color: 'text-green-700', bg: 'bg-green-50', border: 'border-green-200', icon: 'ri-checkbox-circle-line' },
              { label: 'Beklemede', value: pendingCount, color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200', icon: 'ri-time-line' },
              { label: 'İndekslenmedi', value: notIndexedCount, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', icon: 'ri-close-circle-line' },
            ].map(s => (
              <div key={s.label} className={`${s.bg} border ${s.border} rounded-xl p-4 flex items-center gap-3`}>
                <div className={`w-9 h-9 flex items-center justify-center rounded-lg bg-white ${s.color}`}>
                  <i className={`${s.icon} text-base`} />
                </div>
                <div>
                  <div className={`text-2xl font-black ${s.color}`}>{s.value}</div>
                  <div className="text-xs text-gray-400">{s.label}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Disclaimer */}
          <div className="flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-xl p-3 text-xs text-blue-700">
            <i className="ri-information-line mt-0.5 flex-shrink-0" />
            <p>
              Bu veriler <strong>tahmini simülasyon</strong> içerir. Gerçek indeks durumu için
              <a href="https://search.google.com/search-console" target="_blank" rel="noopener noreferrer nofollow" className="underline mx-1 font-semibold">Google Search Console</a>
              üzerinden doğrudan kontrol edin.
            </p>
          </div>

          {/* URL Inspector */}
          <div className="bg-white border border-gray-100 rounded-xl p-4">
            <h4 className="font-bold text-[#0A0E27] text-sm mb-3 flex items-center gap-2">
              <i className="ri-search-line" />URL Denetleyici
            </h4>
            <div className="flex gap-2">
              <input
                type="text"
                value={inspectUrl}
                onChange={e => setInspectUrl(e.target.value)}
                placeholder={`${SITE_URL}/haber/...`}
                className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-[#0A0E27]"
              />
              <a
                href={`https://search.google.com/search-console/inspect?resource_id=${encodeURIComponent(SITE_URL + '/')}&url=${encodeURIComponent(inspectUrl || SITE_URL)}`}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="flex items-center gap-1.5 bg-[#0A0E27] text-white text-xs font-bold px-4 py-2 rounded-lg hover:bg-[#0A0E27]/80 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-external-link-line" />GSC'de İncele
              </a>
            </div>
          </div>

          {/* Pages Table */}
          {loadingNews ? (
            <div className="text-center py-8 text-gray-400">
              <i className="ri-loader-4-line animate-spin text-2xl block mb-2" />Yükleniyor...
            </div>
          ) : (
            <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                <h4 className="font-bold text-[#0A0E27] text-sm">Son 20 Haber — İndeks Durumu</h4>
                <span className="text-xs text-gray-400">{pages.length} sayfa</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100">
                      <th className="text-left px-4 py-2.5 font-semibold text-gray-400">Başlık</th>
                      <th className="text-center px-3 py-2.5 font-semibold text-gray-400 whitespace-nowrap">Durum</th>
                      <th className="text-center px-3 py-2.5 font-semibold text-gray-400 whitespace-nowrap">Tıklama</th>
                      <th className="text-center px-3 py-2.5 font-semibold text-gray-400 whitespace-nowrap">Gösterim</th>
                      <th className="text-center px-3 py-2.5 font-semibold text-gray-400 whitespace-nowrap">Pozisyon</th>
                      <th className="text-center px-3 py-2.5 font-semibold text-gray-400">İşlem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pages.map(p => {
                      const s = statusConfig[p.status];
                      return (
                        <tr key={p.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-3 max-w-[260px]">
                            <p className="font-medium text-[#0A0E27] line-clamp-1">{p.title}</p>
                            <p className="text-gray-400 text-[10px] mt-0.5 truncate">/haber/{p.id.slice(0, 8)}...</p>
                          </td>
                          <td className="px-3 py-3 text-center">
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold ${s.bg} ${s.color}`}>
                              <i className={s.icon} />{s.label}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-center font-semibold text-[#0A0E27]">{p.clicks?.toLocaleString()}</td>
                          <td className="px-3 py-3 text-center text-gray-500">{p.impressions?.toLocaleString()}</td>
                          <td className="px-3 py-3 text-center">
                            <span className={`font-bold ${(p.position || 99) <= 10 ? 'text-green-600' : (p.position || 99) <= 20 ? 'text-amber-600' : 'text-gray-400'}`}>
                              #{p.position}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-center">
                            <a
                              href={`https://search.google.com/search-console/inspect?resource_id=${encodeURIComponent(SITE_URL + '/')}&url=${encodeURIComponent(p.url)}`}
                              target="_blank"
                              rel="noopener noreferrer nofollow"
                              className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#0A0E27] bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                            >
                              <i className="ri-search-line" />İncele
                            </a>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── URL GÖNDER ─── */}
      {activeSection === 'submit' && (
        <div className="flex flex-col gap-4">
          {/* Bulk submit */}
          <div className="bg-white border border-gray-100 rounded-xl p-5">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h4 className="font-bold text-[#0A0E27] text-sm mb-1">Toplu İndeksleme İsteği</h4>
                <p className="text-xs text-gray-500">
                  İndekslenmemiş veya beklemedeki tüm sayfaları Google&apos;a bildirin. GSC&apos;de manuel olarak &quot;İndekslemeyi İste&quot; yapmanız gerekir.
                </p>
              </div>
              {!bulkDone ? (
                <button
                  onClick={handleBulkIndexRequest}
                  disabled={bulkSubmitting || notIndexedCount + pendingCount === 0}
                  className="flex items-center gap-2 bg-[#0A0E27] text-white text-xs font-bold px-5 py-2.5 rounded-lg hover:bg-[#0A0E27]/80 transition-colors cursor-pointer whitespace-nowrap flex-shrink-0 disabled:opacity-50"
                >
                  {bulkSubmitting ? (
                    <><i className="ri-loader-4-line animate-spin" />{notIndexedCount + pendingCount} URL İşleniyor...</>
                  ) : (
                    <><i className="ri-send-plane-line" />{notIndexedCount + pendingCount} URL Gönder</>
                  )}
                </button>
              ) : (
                <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2 flex-shrink-0">
                  <i className="ri-checkbox-circle-fill text-green-600" />
                  <span className="text-xs font-bold text-green-700">Gönderildi!</span>
                </div>
              )}
            </div>

            {/* URL List */}
            {indexRequestUrls.length > 0 && (
              <div className="bg-gray-50 rounded-lg p-3 max-h-48 overflow-y-auto">
                <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-wider mb-2">
                  {indexRequestUrls.length} URL GSC'ye bildirildi
                </p>
                <div className="flex flex-col gap-1">
                  {indexRequestUrls.map((url, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <i className="ri-checkbox-circle-line text-green-500 text-xs flex-shrink-0" />
                      <span className="text-[10px] text-gray-600 truncate">{url}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Manual single URL submission */}
          <div className="bg-white border border-gray-100 rounded-xl p-5">
            <h4 className="font-bold text-[#0A0E27] text-sm mb-3">Tek URL Gönder</h4>
            <p className="text-xs text-gray-500 mb-3">
              Belirli bir haberin URL&apos;sini Google&apos;a doğrudan bildirmek için:
            </p>
            <div className="flex flex-col gap-2">
              {news.slice(0, 5).map(n => (
                <div key={n.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-[#0A0E27] line-clamp-1">{n.title}</p>
                    <p className="text-[10px] text-gray-400 mt-0.5 truncate">{SITE_URL}/haber/{n.id}</p>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button
                      onClick={() => copy(`${SITE_URL}/haber/${n.id}`, n.id)}
                      className="w-7 h-7 flex items-center justify-center bg-white border border-gray-200 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                    >
                      <i className={copiedKey === n.id ? 'ri-check-line text-green-500 text-xs' : 'ri-clipboard-line text-gray-400 text-xs'} />
                    </button>
                    <a
                      href={`https://search.google.com/search-console/inspect?resource_id=${encodeURIComponent(SITE_URL + '/')}&url=${encodeURIComponent(`${SITE_URL}/haber/${n.id}`)}`}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="flex items-center gap-1 bg-[#0A0E27] text-white text-[10px] font-bold px-2.5 py-1.5 rounded-lg hover:bg-[#0A0E27]/80 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-external-link-line" />GSC'de Aç
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Indexing API note */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3">
            <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-lightbulb-line text-amber-600 text-lg" />
            </div>
            <div>
              <p className="text-sm font-semibold text-amber-800 mb-1">Google Indexing API Hakkında</p>
              <p className="text-xs text-amber-700 leading-relaxed">
                Haber siteleri için Google, <strong>Indexing API</strong> aracılığıyla anlık bildirim gönderilmesine izin verir.
                Bu API ile yeni haberleriniz yayınlandığı anda Google&apos;a bildirilebilir. Kurulum için
                <a href="https://developers.google.com/search/apis/indexing-api/v3/quickstart" target="_blank" rel="noopener noreferrer nofollow" className="underline font-semibold mx-1">Google Developers dokümantasyonunu</a>
                inceleyin ve bir servis hesabı oluşturun.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
